import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Logo from '../../assets/images/logo.svg';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setUser } from '../../store/slices/users.slice';
import { BASE_URL, envConfig } from '../../config';
import { useAppTheme } from '../../context/ThemeContext';
import { authClient } from '../../lib/auth-client';

const LoginValidationSchema = Yup.object().shape({
  identifier: Yup.string()
    .max(255)
    .required('Email or username is required')
    .test('email-or-username', 'Enter a valid email or username (min 2 chars)', (value) => {
      if (!value) return false;
      return value.includes('@') ? Yup.string().email().isValidSync(value) : value.length >= 2;
    }),
  password: Yup.string().max(255).required('Password is required'),
});

const AuthLogin = () => {
  const navigator = useNavigate();
  const dispatch = useDispatch();
  const { isNewTheme, accentColor } = useAppTheme();
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((v) => !v);
  const handleMouseDownPassword = (event: any) => event.preventDefault();

  const fetchMe = async () => {
    const res = await fetch(BASE_URL + 'auth/me', {
      credentials: 'include',
      headers: { 'api-key': envConfig.API_KEY },
    });
    if (!res.ok) throw new Error('Could not fetch user profile');
    return res.json();
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { identifier: '', password: '', submit: null },
    validationSchema: LoginValidationSchema,
    onSubmit: async (values, { setErrors, setStatus, setSubmitting }) => {
      try {
        setStatus({ success: false });
        setSubmitting(true);

        const isEmail = values.identifier.includes('@');
        const { error } = isEmail
          ? await authClient.signIn.email({ email: values.identifier, password: values.password })
          : await (authClient.signIn as any).username({ username: values.identifier, password: values.password });

        if (error) {
          setErrors({ submit: error.message ?? 'Invalid credentials' });
          return;
        }

        // Fetch full profile (role, permissions, _id, username) from our users collection
        const user = await fetchMe();
        dispatch(setUser(user));
        navigator('/');
      } catch (err: any) {
        setStatus({ success: false });
        setErrors({ submit: err.message });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleGoogleSignIn = async () => {
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: window.location.origin + '/',
    });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper
        sx={{
          p: isNewTheme ? 4 : 2,
          width: isNewTheme ? 420 : 400,
          ...(isNewTheme
            ? {
                borderRadius: '8px',
                boxShadow: '0 8px 32px rgba(15,23,42,0.10)',
                border: '1px solid #E2E8F0',
                background: '#ffffff',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  height: 3,
                  background: '#0F172A',
                },
              }
            : {}),
        }}>
        {!isNewTheme && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src={Logo} alt="5milah" width={60} height={60} />
          </Box>
        )}

        {isNewTheme && (
          <Stack spacing={2} mb={3} alignItems="center">
            <Box
              sx={{
                width: 64, height: 64, borderRadius: '12px',
                backgroundColor: '#ffffff', border: '1px solid #E2E8F0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(15,23,42,0.05)',
              }}>
              <img src={Logo} alt="5milah" width={44} height={44} />
            </Box>
            <Stack spacing={0.25} alignItems="center">
              <Typography variant="h5" fontWeight={700} color="text.primary">Welcome back</Typography>
              <Typography variant="body2" color="text.secondary">Sign in to your 5milah dashboard</Typography>
            </Stack>
          </Stack>
        )}

        <form noValidate onSubmit={formik.handleSubmit}>
          <Grid container width={isNewTheme ? undefined : 400} spacing={isNewTheme ? 2.5 : 3}>
            <Grid item xs={12}>
              <Stack spacing={isNewTheme ? 0.75 : 1}>
                <InputLabel htmlFor="identifier-login" sx={isNewTheme ? { fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' } : {}}>
                  Email or Username
                </InputLabel>
                <OutlinedInput
                  id="identifier-login"
                  type="text"
                  value={formik.values.identifier}
                  name="identifier"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="Enter email or username"
                  fullWidth
                  error={Boolean(formik.touched.identifier && formik.errors.identifier)}
                />
                {formik.touched.identifier && formik.errors.identifier && (
                  <FormHelperText error>{formik.errors.identifier}</FormHelperText>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={isNewTheme ? 0.75 : 1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <InputLabel htmlFor="password-login" sx={isNewTheme ? { fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' } : {}}>
                    Password
                  </InputLabel>
                  <Link component={RouterLink} to="/forgot-password" variant="caption" color="text.secondary" underline="hover">
                    Forgot password?
                  </Link>
                </Box>
                <OutlinedInput
                  fullWidth
                  error={Boolean(formik.touched.password && formik.errors.password)}
                  id="password-login"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.password}
                  name="password"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        size="large"
                        sx={isNewTheme ? { color: accentColor } : {}}>
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                  placeholder="Enter password"
                />
                {formik.touched.password && formik.errors.password && (
                  <FormHelperText error>{formik.errors.password}</FormHelperText>
                )}
              </Stack>
            </Grid>

            {formik.errors.submit && (
              <Grid item xs={12}>
                <FormHelperText error sx={isNewTheme ? { fontWeight: 600 } : {}}>
                  {formik.errors.submit as string}
                </FormHelperText>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                disableElevation
                disabled={formik.isSubmitting}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                sx={isNewTheme ? { mt: 0.5, py: 1.25, fontSize: '0.9375rem', fontWeight: 600 } : {}}>
                {formik.isSubmitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }}>
                <Typography variant="caption" color="text.secondary">or</Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                onClick={handleGoogleSignIn}
                startIcon={
                  <svg width="18" height="18" viewBox="0 0 18 18">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                }
                sx={{ fontWeight: 600, textTransform: 'none' }}>
                Continue with Google
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default AuthLogin;
